import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    Snackbar,
    Alert,
    Autocomplete,
    Box,
    Typography,
    CircularProgress,
    Button
} from '@mui/material';

import InputMontoMui from '../InputMontoMui';
import{ crearGastoMedico } from '../../services/gastoMedicoService';
import { listarMedicos } from '../../services/medicoService';
import { getTramites } from '../../services/tramiteService';
import { getClientes } from '../../services/clienteService';
import { getTipoTramites } from '../../services/tipoTramiteService';

const ModalAgregarGastoMedico = ({
    open,
    onClose,
    onGastoAgregado,
    preSelectedMedico = null,
    preSelectedTramite = null
}) => {
    const [formData, setFormData] = useState({
        monto: '',
        fecha: new Date().toISOString().split('T')[0],
        idMedico: '',
        idTramite: '',
        comentario: ''
    });

    const [medicos, setMedicos] = useState([]);
    const [tramites, setTramites] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [tiposTramite, setTiposTramite] = useState([]);

    const [loading, setLoading] = useState(false);
    const [cargandoCatalogos, setCargandoCatalogos] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [errors, setErrors] = useState({});

    // Sincronización de props y estado
    useEffect(() => {
        if (open) {
            setFormData(prev => ({
                ...prev,
                idMedico: preSelectedMedico || prev.idMedico || '',
                idTramite: preSelectedTramite || prev.idTramite || '',
                fecha: new Date().toISOString().split('T')[0]
            }));
            cargarCatalogos();
        }
    }, [open, preSelectedMedico, preSelectedTramite]);

    const cargarCatalogos = async () => {
        try {
            setCargandoCatalogos(true);
            const [medicosData, tramitesData, clientesData, tiposTramiteData] = await Promise.all([
                listarMedicos(true),
                getTramites(),
                getClientes(),
                getTipoTramites()
            ]);
            setMedicos(medicosData);
            setTramites(tramitesData);
            setClientes(clientesData);
            setTiposTramite(tiposTramiteData);
        } catch (error) {
            setSnackbar({ open: true, message: 'Error al cargar los catálogos', severity: 'error' });
        } finally {
            setCargandoCatalogos(false);
        }
    };

    const obtenerNombreCliente = (cuil) => {
        const cliente = clientes.find(c => c.cuil === cuil);
        return cliente ? `${cliente.nombre} ${cliente.apellido}` : cuil;
    };

    const obtenerTipoTramite = (idTipoTramite) => {
        const tipo = tiposTramite.find(t => t.idTipoTramite === idTipoTramite);
        return tipo ? tipo.nombre : 'N/A';
    };

    const formatearOpcionTramite = (tramite) => {
        if (!tramite) return "";
        const nombreCliente = obtenerNombreCliente(tramite.idCliente);
        const tipoTramite = obtenerTipoTramite(tramite.idTipoTramite);
        // Retornamos el string que queremos que se vea en el input
        return `${nombreCliente} (${tipoTramite})`;
    };

    const validarFormulario = () => {
        const nuevosErrores = {};
        if (!formData.monto || parseFloat(formData.monto) <= 0) {
            nuevosErrores.monto = 'El monto debe ser mayor a 0';
        }
        if (!formData.fecha) {
            nuevosErrores.fecha = 'La fecha es requerida';
        }
        if (!formData.idTramite) {
            nuevosErrores.idTramite = 'Debe seleccionar un trámite';
        }
        setErrors(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleAutocompleteChange = (name, newValue) => {
        const value = newValue ? (name === 'idTramite' ? newValue.idTramite : newValue.idMedico) : '';
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validarFormulario()) return;

        setLoading(true);
        try {
            const gastoData = {
                monto: parseFloat(formData.monto),
                fecha: formData.fecha,
                idTramite: formData.idTramite,
                ...(formData.idMedico && { idMedico: formData.idMedico }),
                ...(formData.comentario && { comentario: formData.comentario })
            };

            await crearGastoMedico(gastoData);
            setSnackbar({ open: true, message: 'Gasto agregado correctamente', severity: 'success' });

            setTimeout(() => {
                handleClose();
                if (onGastoAgregado) onGastoAgregado();
            }, 1000);
        } catch (error) {
            setSnackbar({ open: true, message: 'Error al agregar el gasto', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            monto: '',
            fecha: new Date().toISOString().split('T')[0],
            idMedico: '',
            idTramite: '',
            comentario: ''
        });
        setErrors({});
        onClose();
    };

    const tramiteSeleccionado = tramites.find(
        t => String(t.idTramite) === String(preSelectedTramite)
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                    textAlign: 'center'
                }}>
                    Agregar Nuevo Gasto
                </DialogTitle>

                <form onSubmit={handleSubmit}>
                    <DialogContent sx={{ mt: 2 }}>
                        {cargandoCatalogos ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputMontoMui
                                        label="Monto *"
                                        value={formData.monto}
                                        onChange={(v) => {
                                            setFormData(prev => ({ ...prev, monto: v }));
                                            if (errors.monto) setErrors(prev => ({ ...prev, monto: '' }));
                                        }}
                                        error={!!errors.monto}
                                        helperText={errors.monto}
                                        required
                                    />
                                </FormControl>

                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <TextField
                                        label="Fecha del gasto *"
                                        name="fecha"
                                        type="date"
                                        value={formData.fecha}
                                        onChange={handleChange}
                                        error={!!errors.fecha}
                                        helperText={errors.fecha}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </FormControl>

                               {/* Trámite con Autocomplete */}
                               {preSelectedTramite ? (
                                   <TextField
                                       fullWidth
                                       label="Trámite"
                                       value={tramiteSeleccionado ? formatearOpcionTramite(tramiteSeleccionado) : ""}
                                       InputProps={{
                                           readOnly: true
                                       }}
                                       sx={{
                                           mb: 3,
                                           "& .MuiInputBase-input": {
                                               color: "text.secondary"
                                           }
                                       }}
                                       helperText="Trámite asociado al cliente"
                                   />
                               ) : (
                                   <Autocomplete
                                       fullWidth
                                       options={tramites}
                                       getOptionLabel={(option) => formatearOpcionTramite(option)}
                                       value={tramites.find(t => t.idTramite === formData.idTramite) || null}
                                       onChange={(event, newValue) =>
                                           handleAutocompleteChange("idTramite", newValue)
                                       }
                                       renderInput={(params) => (
                                           <TextField
                                               {...params}
                                               label="Trámite *"
                                               error={!!errors.idTramite}
                                               helperText={errors.idTramite || "Busque por nombre de cliente"}
                                               required
                                           />
                                       )}
                                       sx={{ mb: 3 }}
                                       isOptionEqualToValue={(option, value) =>
                                           option.idTramite === value.idTramite
                                       }
                                   />
                               )}

                                <Autocomplete
                                    fullWidth
                                    options={medicos}
                                    getOptionLabel={(option) => `${option.nombre} ${option.apellido}`}
                                    value={medicos.find(m => m.idMedico === formData.idMedico) || null}
                                    onChange={(event, newValue) => handleAutocompleteChange('idMedico', newValue)}
                                    disabled={!!preSelectedMedico} // BLOQUEO SI VIENE PRESELECCIONADO
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Médico"
                                            placeholder="Buscar por nombre..."
                                        />
                                    )}
                                    sx={{ mb: 3 }}
                                    isOptionEqualToValue={(option, value) => option.idMedico === value.idMedico}
                                />

                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <TextField
                                        label="Comentario"
                                        name="comentario"
                                        value={formData.comentario}
                                        onChange={handleChange}
                                        multiline
                                        rows={3}
                                        placeholder="Descripción del gasto (opcional)..."
                                    />
                                </FormControl>

                                <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', color: 'text.secondary' }}>
                                    * Campos requeridos
                                </Typography>
                            </>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ p: 3, gap: 1 }}>
                        <Button onClick={handleClose} disabled={loading}>
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading || cargandoCatalogos}
                            sx={{ borderRadius: '20px' }}
                        >
                            {loading ? 'Guardando...' : 'Guardar Gasto'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ModalAgregarGastoMedico;